import React, { useState } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { getDatesBetween } from '../../../../../utils/calculations/date';

/**
 * Компонент визуализации календаря с отмеченными датами
 */
const CalendarView = ({ results, formData }) => {
  // Установка начальных значений для состояний
  const initialMonth = results ? results.resultDate.getMonth() : new Date().getMonth();
  const initialYear = results ? results.resultDate.getFullYear() : new Date().getFullYear();
  
  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [currentYear, setCurrentYear] = useState(initialYear);

  // Если нет результатов - не отображаем календарь
  if (!results) return null;

  // Функция перехода к следующему месяцу
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Функция перехода к предыдущему месяцу
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  // Получаем даты между начальной и результирующей
  let datesBetween = [];
  if (results.operation.mode === 'add') {
    datesBetween = getDatesBetween(results.startDate, results.resultDate);
  } else {
    datesBetween = getDatesBetween(results.resultDate, results.startDate);
  }

  // Получаем даты для текущего месяца отображения
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  
  // Готовим массив недель для отображения
  const weeks = [];
  let days = [];
  
  // Добавляем пустые дни в начале месяца
  for (let i = 0; i < (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1); i++) {
    days.push(null);
  }
  
  // Добавляем дни месяца
  monthDays.forEach(day => {
    days.push(new Date(currentYear, currentMonth, day));
    
    // Если достигли воскресенья или конца месяца, начинаем новую неделю
    if (days.length === 7 || day === daysInMonth) {
      // Если не завершилась неделя, добавляем пустые дни в конце
      while (days.length < 7) {
        days.push(null);
      }
      weeks.push([...days]);
      days = [];
    }
  });

  // Названия месяцев
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель',
    'Май', 'Июнь', 'Июль', 'Август',
    'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  // Сокращенные названия дней недели (начиная с понедельника)
  const weekdayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Функция для определения, является ли день выходным
  const isWeekend = (date) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6; // 0 - воскресенье, 6 - суббота
  };

  // Функция для определения, является ли день праздником
  const isHoliday = (date) => {
    if (!date || !results.holidays || !formData.includeHolidays) return false;
    
    return results.holidays.some(holiday => 
      holiday.getDate() === date.getDate() && 
      holiday.getMonth() === date.getMonth() && 
      holiday.getFullYear() === date.getFullYear()
    );
  };

  // Функция для определения, входит ли дата в диапазон между начальной и результирующей
  const isInRange = (date) => {
    if (!date) return false;
    
    // Проверяем, не является ли это начальной или результирующей датой
    if (
      date.getDate() === results.startDate.getDate() && 
      date.getMonth() === results.startDate.getMonth() && 
      date.getFullYear() === results.startDate.getFullYear()
    ) {
      return false; // Это стартовая дата, обрабатываем отдельно
    }
    
    if (
      date.getDate() === results.resultDate.getDate() && 
      date.getMonth() === results.resultDate.getMonth() && 
      date.getFullYear() === results.resultDate.getFullYear()
    ) {
      return false; // Это результирующая дата, обрабатываем отдельно
    }
    
    // Проверяем, входит ли дата в диапазон между начальной и результирующей
    return datesBetween.some(d => 
      d.getDate() === date.getDate() && 
      d.getMonth() === date.getMonth() && 
      d.getFullYear() === date.getFullYear()
    );
  };

  // Функция для определения класса дня
  const getDayClass = (date) => {
    if (!date) return '';
    
    const baseClass = 'calendar-day';
    
    // Проверяем, является ли день текущим
    const isToday = 
      date.getDate() === new Date().getDate() &&
      date.getMonth() === new Date().getMonth() &&
      date.getFullYear() === new Date().getFullYear();
    
    // Проверяем, является ли день начальной датой
    const isStartDate = 
      date.getDate() === results.startDate.getDate() && 
      date.getMonth() === results.startDate.getMonth() && 
      date.getFullYear() === results.startDate.getFullYear();
    
    // Проверяем, является ли день результирующей датой
    const isResultDate = 
      date.getDate() === results.resultDate.getDate() && 
      date.getMonth() === results.resultDate.getMonth() && 
      date.getFullYear() === results.resultDate.getFullYear();
    
    // Собираем классы
    let classes = [baseClass];
    
    if (isStartDate) {
      classes.push('calendar-day-start');
    } else if (isResultDate) {
      classes.push('calendar-day-end');
    } else if (isInRange(date)) {
      classes.push('calendar-day-highlight');
    }
    
    if (isWeekend(date)) {
      classes.push('calendar-day-weekend');
    }
    
    if (isHoliday(date)) {
      classes.push('calendar-day-holiday');
    }
    
    if (isToday) {
      classes.push('border-2 border-blue-500');
    }
    
    return classes.join(' ');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <CalendarIcon className="w-5 h-5 mr-2 text-cyan-500" />
          Календарь
        </h3>
        
        <div className="flex space-x-1">
          <button
            onClick={prevMonth}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Предыдущий месяц"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <span className="px-2 py-1 text-gray-700 dark:text-gray-300 min-w-[120px] text-center">
            {monthNames[currentMonth]} {currentYear}
          </span>
          <button
            onClick={nextMonth}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Следующий месяц"
          >
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Пояснение к календарю */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>В календаре отмечены: начальная дата, результат и период между ними. 
            {formData.includeWorkingDays && ' Учитываются только рабочие дни (пн-пт).'}
            {formData.includeHolidays && ' Праздничные дни отмечены красным.'}
          </p>
        </div>
      </div>

      {/* Календарь */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {weekdayNames.map((day, index) => (
                <th key={index} className={`text-center py-2 text-sm font-medium ${index >= 5 ? 'text-red-500 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={weekIndex}>
                {week.map((date, dayIndex) => (
                  <td key={dayIndex} className="p-1 text-center">
                    {date ? (
                      <div 
                        className={getDayClass(date)}
                        title={`${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`}
                      >
                        {date.getDate()}
                      </div>
                    ) : (
                      <div className="w-8 h-8"></div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Легенда */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-xs">
        <div className="flex items-center">
          <div className="calendar-day-start w-6 h-6 mr-2"></div>
          <span className="text-gray-700 dark:text-gray-300">Начальная дата</span>
        </div>
        <div className="flex items-center">
          <div className="calendar-day-end w-6 h-6 mr-2"></div>
          <span className="text-gray-700 dark:text-gray-300">Результат</span>
        </div>
        <div className="flex items-center">
          <div className="calendar-day-highlight w-6 h-6 mr-2"></div>
          <span className="text-gray-700 dark:text-gray-300">Период</span>
        </div>
        <div className="flex items-center">
          <div className="calendar-day-holiday w-6 h-6 mr-2"></div>
          <span className="text-gray-700 dark:text-gray-300">Праздник</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;